import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdManager from '@/components/AdManager';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, LogOut, Newspaper, Lock, Trash2, 
  Calendar, Eye, List, Image as ImageIcon 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KANNADA_MONTHS = [
  'ಜನವರಿ', 'ಫೆಬ್ರವರಿ', 'ಮಾರ್ಚ್', 'ಏಪ್ರಿಲ್', 'ಮೇ', 'ಜೂನ್',
  'ಜುಲೈ', 'ಆಗಸ್ಟ್', 'ಸೆಪ್ಟೆಂಬರ್', 'ಅಕ್ಟೋಬರ್', 'ನವೆಂಬರ್', 'ಡಿಸೆಂಬರ್'
];

const generateDefaultTitle = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const day = d.getDate();
  const month = KANNADA_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `ಕಲ್ಲಿನ ಕೂಗು - ${day} ${month} ${year}`;
};

const AdminDashboard = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [newspaperData, setNewspaperData] = useState({
    date: new Date().toISOString().split('T')[0],
    title: generateDefaultTitle(new Date().toISOString().split('T')[0]),
    pdf: null
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [editions, setEditions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
    } else {
      fetchEditions();
    }
  }, [navigate]);

  const fetchEditions = async () => {
    try {
      const response = await axios.get(`${API}/editions`);
      setEditions(response.data);
    } catch (error) {
      console.error('Error fetching editions:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleDateChange = (newDate) => {
    setNewspaperData({
      ...newspaperData,
      date: newDate,
      title: generateDefaultTitle(newDate)
    });
  };

  const handleNewspaperUpload = async (e) => {
    e.preventDefault();
    setUploading(true);

    const formData = new FormData();
    formData.append('date', newspaperData.date);
    formData.append('title', newspaperData.title || generateDefaultTitle(newspaperData.date));
    formData.append('pdf', newspaperData.pdf);

    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/editions`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success(language === 'kn' ? 'ಪತ್ರಿಕೆ ಯಶಸ್ವಿಯಾಗಿ ಅಪ್‌ಲೋಡ್ ಆಯಿತು' : 'Newspaper uploaded successfully');
      setNewspaperData({
        date: new Date().toISOString().split('T')[0],
        title: generateDefaultTitle(new Date().toISOString().split('T')[0]),
        pdf: null
      });
      const fileInput = document.getElementById('pdf-input');
      if (fileInput) fileInput.value = '';
      fetchEditions();
    } catch (error) {
      toast.error(language === 'kn' ? 'ಅಪ್‌ಲೋಡ್ ವಿಫಲವಾಗಿದೆ' : 'Failed to upload newspaper');
      if (error.response?.status === 401) handleLogout();
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteEdition = async (editionId, editionTitle) => {
    if (!window.confirm(language === 'kn'
      ? `"${editionTitle}" ಅನ್ನು ಅಳಿಸಬೇಕೇ? ಈ ಕ್ರಿಯೆಯನ್ನು ರದ್ದುಗೊಳಿಸಲಾಗುವುದಿಲ್ಲ.`
      : `Delete "${editionTitle}"? This action cannot be undone.`
    )) return;

    setDeleting(editionId);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/editions/${editionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(language === 'kn' ? 'ಪತ್ರಿಕೆ ಅಳಿಸಲಾಗಿದೆ' : 'Edition deleted successfully');
      fetchEditions();
    } catch (error) {
      toast.error(language === 'kn' ? 'ಅಳಿಸಲು ವಿಫಲವಾಗಿದೆ' : 'Failed to delete edition');
      if (error.response?.status === 401) handleLogout();
    } finally {
      setDeleting(null);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(language === 'kn' ? 'ಪಾಸ್‌ವರ್ಡ್‌ಗಳು ಹೊಂದಿಕೆಯಾಗುವುದಿಲ್ಲ' : 'Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error(language === 'kn' ? 'ಕನಿಷ್ಠ 8 ಅಕ್ಷರಗಳು ಬೇಕು' : 'Password must be at least 8 characters');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(`${API}/admin/change-password`, {
        old_password: passwordData.oldPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(language === 'kn' ? 'ಪಾಸ್‌ವರ್ಡ್ ಬದಲಾಯಿಸಲಾಗಿದೆ' : 'Password changed successfully. Please login again.');
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => handleLogout(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* HEADER SECTION */}
      <div className="bg-[#0F172A] text-white py-4 shadow-xl">
        <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
          <h1
            className="text-lg sm:text-2xl md:text-3xl font-bold truncate"
            style={{ fontFamily: 'Noto Serif Kannada, serif' }}
          >
            ಕಲ್ಲಿನ ಕೂಗು - {language === 'kn' ? 'ನಿರ್ವಾಹಕ ಪುಟ' : 'Admin Portal'}
          </h1>
          <Button
            variant="destructive"
            onClick={handleLogout}
            className="shrink-0 font-bold shadow-md hover:scale-[1.02] transition-transform"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('logout')}
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8">
        <Tabs defaultValue="newspaper" className="w-full">
          {/* NAVIGATION TABS */}
          <TabsList className="grid w-full grid-cols-4 max-w-4xl mx-auto bg-white border border-gray-100 p-1 rounded-2xl shadow-sm mb-10">
            <TabsTrigger value="newspaper" className="rounded-xl data-[state=active]:text-red-600 data-[state=active]:bg-red-50">
              <Upload className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Upload</span>
            </TabsTrigger>
            <TabsTrigger value="manage" className="rounded-xl data-[state=active]:text-red-600 data-[state=active]:bg-red-50">
              <List className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Manage</span>
            </TabsTrigger>
            <TabsTrigger value="ads" className="rounded-xl data-[state=active]:text-red-600 data-[state=active]:bg-red-50">
              <ImageIcon className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{language === 'kn' ? 'ಜಾಹೀರಾತು' : 'Ad Center'}</span>
            </TabsTrigger>
            <TabsTrigger value="password" className="rounded-xl data-[state=active]:text-red-600 data-[state=active]:bg-red-50">
              <Lock className="mr-1 sm:mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB CONTENT: UPLOAD NEWSPAPER */}
          <TabsContent value="newspaper">
            <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-10 border border-gray-50">
              <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-8 border-b pb-4">
                {language === 'kn' ? 'ಹೊಸ ಪತ್ರಿಕೆ ಅಪ್‌ಲೋಡ್ ಮಾಡಿ' : 'Publish New Edition'}
              </h2>
              <form onSubmit={handleNewspaperUpload} className="space-y-8">
                <div className="space-y-2">
                  <Label htmlFor="date" className="font-bold text-gray-600">{t('date')}</Label>
                  <Input
                    id="date"
                    type="date"
                    value={newspaperData.date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    required
                    className="rounded-xl border-gray-200 h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title" className="font-bold text-gray-600">{t('title')}</Label>
                  <Input
                    id="title"
                    type="text"
                    value={newspaperData.title}
                    onChange={(e) => setNewspaperData({ ...newspaperData, title: e.target.value })}
                    placeholder={generateDefaultTitle(newspaperData.date)}
                    required
                    className="rounded-xl border-gray-200 h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pdf-input" className="font-bold text-gray-600">{t('selectPDF')}</Label>
                  <Input
                    id="pdf-input"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setNewspaperData({ ...newspaperData, pdf: e.target.files[0] })}
                    required
                    className="rounded-xl border-gray-200 h-12 py-2"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#EB1B1B] hover:bg-black text-white text-lg py-8 rounded-2xl shadow-lg shadow-red-100 transition-all active:scale-[0.98]"
                  disabled={uploading}
                >
                  <Upload className="mr-2 h-6 w-6" />
                  {uploading ? t('loading') : (language === 'kn' ? 'ಪತ್ರಿಕೆ ಪ್ರಕಟಿಸಿ' : 'Publish Edition')}
                </Button>
              </form>
            </div>
          </TabsContent>

          {/* TAB CONTENT: MANAGE EDITIONS */}
          <TabsContent value="manage">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl sm:text-2xl font-black text-gray-900 uppercase tracking-widest">
                  {language === 'kn' ? 'ಹಿಂದಿನ ಪತ್ರಿಕೆಗಳ ಪಟ್ಟಿ' : 'Published Editions'}
                </h2>
              </div>
              
              {editions.length === 0 ? (
                <div className="bg-white rounded-3xl shadow-lg p-20 text-center border border-dashed border-gray-200">
                  <Newspaper className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                  <p className="text-lg text-gray-400 font-bold">{language === 'kn' ? 'ಯಾವುದೇ ಪತ್ರಿಕೆಗಳು ಇಲ್ಲ' : 'No editions found'}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {editions.map((edition) => (
                    <div key={edition.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center justify-between gap-4 hover:shadow-lg transition-all">
                      <div className="flex items-center gap-6 min-w-0 flex-1">
                        <div className="bg-gray-900 text-white px-4 py-2 rounded-xl text-center shrink-0">
                          <div className="text-xl font-black leading-tight">{new Date(edition.date).getDate()}</div>
                          <div className="text-[9px] uppercase font-black">{new Date(edition.date).toLocaleDateString('en-IN', { month: 'short' })}</div>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-gray-900 truncate text-sm sm:text-lg font-kannada">{edition.title}</h3>
                          <div className="flex items-center gap-4 text-xs text-gray-400 mt-1 font-bold">
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3 text-red-500" />{new Date(edition.date).toLocaleDateString('en-IN')}</span>
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-red-500" />{edition.views} ವೀಕ್ಷಣೆಗಳು</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteEdition(edition.id, edition.title)}
                        disabled={deleting === edition.id}
                        className="rounded-xl px-4"
                      >
                        <Trash2 className="h-4 w-4 mr-1 sm:mr-2" />
                        <span className="hidden sm:inline font-bold">{deleting === edition.id ? '...' : 'Delete'}</span>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB CONTENT: AD CENTER */}
          <TabsContent value="ads">
            <div className="max-w-4xl mx-auto">
              <AdManager />
            </div>
          </TabsContent>

          {/* TAB CONTENT: SECURITY */}
          <TabsContent value="password">
            <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-10 border border-gray-50">
              <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-8 border-b pb-4">{language === 'kn' ? 'ಪಾಸ್‌ವರ್ಡ್ ಭದ್ರತೆ' : 'Account Security'}</h2>
              <form onSubmit={handlePasswordChange} className="space-y-6">
                <div className="space-y-2">
                  <Label className="font-bold text-gray-600" htmlFor="old-password">{language === 'kn' ? 'ಹಳೆಯ ಪಾಸ್‌ವರ್ಡ್' : 'Current Password'}</Label>
                  <Input id="old-password" type="password" value={passwordData.oldPassword} onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })} required className="rounded-xl h-12" />
                </div>
                <div className="space-y-2">
                  <Label className="font-bold text-gray-600" htmlFor="new-password">{language === 'kn' ? 'ಹೊಸ ಪಾಸ್‌ವರ್ಡ್' : 'New Password'}</Label>
                  <Input id="new-password" type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} required minLength={8} className="rounded-xl h-12" />
                </div>
                <div className="space-y-2">
                  <Label className="font-bold text-gray-600" htmlFor="confirm-password">{language === 'kn' ? 'ದೃಢೀಕರಿಸಿ' : 'Confirm Password'}</Label>
                  <Input id="confirm-password" type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} required minLength={8} className="rounded-xl h-12" />
                </div>
                <Button type="submit" className="w-full bg-gray-900 hover:bg-black text-white text-lg py-8 rounded-2xl transition-all" disabled={uploading}>
                  <Lock className="mr-2 h-5 w-5" />
                  {uploading ? 'Updating...' : (language === 'kn' ? 'ಪಾಸ್‌ವರ್ಡ್ ಬದಲಿಸಿ' : 'Save New Password')}
                </Button>
              </form>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
